import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';
import {add, deleteRecord, fetch, update} from '../services/OrdersService';
import {Order} from '../types';
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications';
import {ordersQueryKeys} from './ordersQueryKeys';

export const useOrder = (orderId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const orderQuery = useQuery({
        queryKey: ordersQueryKeys.detail(orderId),
        queryFn: () => fetch(orderId!),
        enabled: !!orderId
    });

    // ['orders'] is the root of the list AND the detail keys, so one invalidation reaches
    // both (§8).
    const invalidate = () => queryClient.invalidateQueries({queryKey: ordersQueryKeys.all});

    // CREATE MUTATION. The input is whitelisted by the service (buildCreateOrderInput).
    const createMutation = useMutation({
        mutationFn: (values: Order) => add(values),
        onSuccess: () => {
            notifySuccess(I18n.get('Order created'));
            invalidate();
        },
        onError: notifyError
    });

    // UPDATE MUTATION. The id comes from the hook argument or the record being edited, never
    // from a form field; the service whitelists the rest.
    const updateMutation = useMutation({
        mutationFn: (values: Order) => {
            const id = orderId ?? values.id;
            if (!id) throw new Error('orderId is required parameter');
            return update(id, values);
        },
        onSuccess: () => {
            notifySuccess(I18n.get('Order updated'));
            invalidate();
        },
        onError: notifyError
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!orderId) throw new Error('orderId is required parameter');
            return deleteRecord(orderId);
        },
        onSuccess: () => {
            notifySuccess(I18n.get('Order deleted'));
            invalidate();
        },
        onError: notifyError
    });

    // Resolve true once the save lands and false when it is rejected, never throw: the drawer
    // awaits this and closes only on true, so a server-side rejection keeps the operator's
    // input on screen (§6, §7). The mutation's onError has already toasted.
    const settle = (run: Promise<unknown>) => run.then(() => true, () => false);

    return {
        record: orderQuery.data,
        loading: orderQuery.isLoading,
        isError: orderQuery.isError,
        fetchRecord: orderQuery.refetch,
        createRecord: (values: Order) => settle(createMutation.mutateAsync(values)),
        updateRecord: (values: Order) => settle(updateMutation.mutateAsync(values)),
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
